<!--
  AccountApplications.svelte — User's registered applications list.

  Shows all applications the current user is registered with,
  including permission status. Fetches from the profile applications endpoint.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { Badge, Spinner, Heading } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { listMyApplications } from '../../AppNetwork';
	import type { UserApplication } from '../../AppTypes';
	import site from '../../site';
	import { t, t2 } from '../../i18n';

	/** API base URL for image paths. */
	const apiBase = site.backend.baseURL;

	/** User's registered applications. */
	let myApps: UserApplication[] = $state([]);
	/** Whether loading. */
	let loading = $state(true);
	/** Error text. */
	let error = $state('');

	/** Load user's applications (joined application data included). */
	async function loadData(): Promise<void> {
		loading = true;
		error = '';
		/** Fetch the user's registered applications from the profile endpoint. */
		try {
			myApps = await listMyApplications();
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load applications');
		} finally {
			loading = false;
		}
	}

	/** Format a date. */
	function formatDate(dateString: string): string {
		return new Date(dateString).toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		});
	}

	onMount(loadData);
</script>

<Heading tag="h3">{$t("My Applications")}</Heading>

<div class="mt-4 space-y-6">
	{#if error}
		<div class="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
			<i class="fa-solid fa-exclamation-triangle me-2"></i>{error}
		</div>
	{/if}

	{#if loading}
		<div class="flex justify-center py-12">
			<Spinner size="8" />
		</div>
	{:else if myApps.length === 0}
		<AbstractCard class="max-w-none">
			<div class="py-8 text-center">
				<i class="fa-solid fa-cubes mb-4 text-4xl text-gray-300 dark:text-gray-600"></i>
				<p class="text-gray-500 dark:text-gray-400">{$t("You are not registered with any applications yet.")}</p>
			</div>
		</AbstractCard>
	{:else}
		<div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
			{#each myApps as ua (ua.applicationID)}
				{@const app = ua.application}
				<AbstractCard class="max-w-none">
					<div class="flex items-start gap-3">
						<div class="rounded-lg bg-primary-100 p-2 dark:bg-primary-900/30">
							{#if app?.iconImageName}
								<img
									src="{apiBase}/uploads/applications/{app.iconImageName}"
									alt="{app?.applicationName} icon"
									class="h-5 w-5 rounded object-contain dark:invert"
								/>
							{:else if app?.icon}
								<i class="fa-solid {app.icon} text-primary-600 dark:text-primary-400"></i>
							{:else}
								<i class="fa-solid fa-cube text-primary-600 dark:text-primary-400"></i>
							{/if}
						</div>
						<div class="min-w-0 flex-1">
							<h4 class="truncate font-medium text-gray-900 dark:text-white">
								{app?.applicationName || `Application #${ua.applicationID}`}
							</h4>
							{#if app?.description}
								<p class="mt-0.5 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
									{app.description}
								</p>
							{/if}
							<div class="mt-2 flex items-center gap-2">
								{#if ua.isUserPermissionGranted}
									<Badge color="green">{$t("Authorized")}</Badge>
								{:else}
									<Badge color="yellow">{$t("Pending")}</Badge>
								{/if}
								<span class="text-xs text-gray-400 dark:text-gray-500">
									{$t("Joined")} {formatDate(ua.created)}
								</span>
							</div>
							{#if app?.website}
								<a
									href={app.website}
									target="_blank"
									class="mt-1 inline-block text-xs text-primary-600 hover:underline dark:text-primary-400"
								>
									<i class="fa-solid fa-external-link me-1"></i>{app.website}
								</a>
							{/if}
						</div>
					</div>
				</AbstractCard>
			{/each}
		</div>
	{/if}
</div>
